import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Logo from '../components/Logo';
import { useCurrency } from '../context/CurrencyContext';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Toaster, toast } from 'sonner';
import axios from 'axios';
import {
  ChefHat,
  Calendar,
  DollarSign,
  Eye,
  Plus,
  Edit,
  Trash2,
  Users,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  LogOut,
  Home,
  BarChart3,
  UtensilsCrossed,
  Loader2,
  ShieldCheck,
  ToggleLeft,
  Wallet,
  Landmark,
  ArrowDownToLine,
  Save,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const { formatPrice } = useCurrency();
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [experiences, setExperiences] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState(null);
  const [togglingBookable, setTogglingBookable] = useState({});
  const [earnings, setEarnings] = useState(null);
  const [bankDetails, setBankDetails] = useState({ bank_name: '', account_holder: '', account_number: '', sort_code: '' });
  const [bankSaving, setBankSaving] = useState(false);

  const toggleBookable = async (expId, currentValue) => {
    setTogglingBookable(prev => ({ ...prev, [expId]: true }));
    try {
      await axios.put(`${API_URL}/api/experiences/${expId}/bookable`, 
        { bookable: !currentValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExperiences(prev => prev.map(exp => 
        exp.id === expId ? { ...exp, bookable: !currentValue } : exp
      ));
      toast.success(!currentValue ? 'Experience is now bookable' : 'Experience is no longer bookable');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update');
    } finally {
      setTogglingBookable(prev => ({ ...prev, [expId]: false }));
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, expRes, bookingsRes, earningsRes, bankRes] = await Promise.all([
        axios.get(`${API_URL}/api/host/dashboard`, { headers }),
        axios.get(`${API_URL}/api/host/experiences`, { headers }),
        axios.get(`${API_URL}/api/host/bookings`, { headers }),
        axios.get(`${API_URL}/api/host/earnings`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_URL}/api/host/bank-details`, { headers }).catch(() => ({ data: {} })),
      ]);
      setStats(statsRes.data);
      setExperiences(expRes.data);
      setBookings(bookingsRes.data);
      if (earningsRes.data) setEarnings(earningsRes.data);
      if (bankRes.data && bankRes.data.bank_name) setBankDetails(bankRes.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExperience = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API_URL}/api/experiences/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExperiences(experiences.filter(e => e.id !== deleteId));
      toast.success('Experience deleted');
    } catch (error) {
      toast.error('Failed to delete experience');
    } finally {
      setDeleteId(null);
    }
  };

  const handleUpdateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API_URL}/api/bookings/${bookingId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(bookings.map(b => b.id === bookingId ? { ...b, status } : b));
      toast.success(`Booking ${status}`);
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const saveBankDetails = async () => {
    if (!bankDetails.bank_name || !bankDetails.account_holder || !bankDetails.account_number || !bankDetails.sort_code) {
      toast.error('Please fill in all bank details');
      return;
    }
    setBankSaving(true);
    try {
      await axios.put(`${API_URL}/api/host/bank-details`, bankDetails, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bank details saved');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save bank details');
    } finally {
      setBankSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#D65A31]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4EDE4]" data-testid="dashboard-page">
      <Toaster />
      
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white border-r z-40">
        <div className="p-6">
          <Logo size={34} />
        </div>

        <nav className="px-4 space-y-1">
          <button
            onClick={() => setActiveTab('overview')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'overview' ? 'bg-[#FDF8F4] text-[#D65A31]' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="nav-overview"
          >
            <BarChart3 className="w-5 h-5" />
            Overview
          </button>
          <button
            onClick={() => setActiveTab('experiences')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'experiences' ? 'bg-[#FDF8F4] text-[#D65A31]' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="nav-experiences"
          >
            <UtensilsCrossed className="w-5 h-5" />
            My Experiences
          </button>
          <button
            onClick={() => setActiveTab('bookings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'bookings' ? 'bg-[#FDF8F4] text-[#D65A31]' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="nav-bookings"
          >
            <Calendar className="w-5 h-5" />
            Bookings
          </button>
          <button
            onClick={() => setActiveTab('earnings')}
            className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
              activeTab === 'earnings' ? 'bg-[#FDF8F4] text-[#D65A31]' : 'text-gray-600 hover:bg-gray-100'
            }`}
            data-testid="nav-earnings"
          >
            <Wallet className="w-5 h-5" />
            Earnings
          </button>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-10 h-10 bg-[#D65A31] rounded-full flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{user?.name}</p>
              <p className="text-sm text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/" className="flex-1">
              <Button variant="outline" className="w-full" size="sm">
                <Home className="w-4 h-4 mr-1" /> Home
              </Button>
            </Link>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-semibold">Welcome back, {user?.name?.split(' ')[0]}!</h1>
            <p className="text-gray-500">Manage your meal experiences and bookings</p>
          </div>
          <Link to="/dashboard/create">
            <Button className="bg-[#D65A31] hover:bg-[#B84A28] text-white" data-testid="create-experience-btn">
              <Plus className="w-5 h-5 mr-2" />
              Create Experience
            </Button>
          </Link>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-8" data-testid="overview-tab">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <ChefHat className="w-8 h-8 text-[#D65A31]" />
                  <span className="text-sm text-green-600 bg-green-100 px-2 py-1 rounded-full">
                    {stats.active_experiences} active
                  </span>
                </div>
                <p className="text-3xl font-bold" data-testid="total-experiences">{stats.total_experiences}</p>
                <p className="text-gray-500">Total Experiences</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <Calendar className="w-8 h-8 text-[#D65A31]" />
                  <span className="text-sm text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full">
                    {stats.pending_bookings} pending
                  </span>
                </div>
                <p className="text-3xl font-bold" data-testid="total-bookings">{stats.total_bookings}</p>
                <p className="text-gray-500">Total Bookings</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <DollarSign className="w-8 h-8 text-[#D65A31]" />
                </div>
                <p className="text-3xl font-bold" data-testid="total-revenue">{formatPrice(stats.total_revenue)}</p>
                <p className="text-gray-500">Total Revenue</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm">
                <div className="flex items-center justify-between mb-4">
                  <Eye className="w-8 h-8 text-[#D65A31]" />
                </div>
                <p className="text-3xl font-bold" data-testid="total-views">{stats.total_views.toLocaleString()}</p>
                <p className="text-gray-500">Total Views</p>
              </div>
            </div>

            {/* Recent Bookings */}
            <div className="bg-white rounded-xl shadow-sm">
              <div className="p-6 border-b">
                <h2 className="text-lg font-semibold">Recent Bookings</h2>
              </div>
              {bookings.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>No bookings yet</p>
                </div>
              ) : (
                <div className="divide-y">
                  {bookings.slice(0, 5).map((booking) => (
                    <div key={booking.id} className="p-4 flex items-center justify-between">
                      <div>
                        <p className="font-medium">{booking.guest_name}</p>
                        <p className="text-sm text-gray-500">{booking.experience_title}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{formatPrice(booking.total_price)}</p>
                        <p className="text-sm text-gray-500">{booking.date}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-sm ${
                        booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                        booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                        booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {booking.status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Experiences Tab */}
        {activeTab === 'experiences' && (
          <div className="space-y-6" data-testid="experiences-tab">
            {experiences.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                <ChefHat className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h2 className="text-xl font-semibold mb-2">No experiences yet</h2>
                <p className="text-gray-500 mb-6">Create your first meal experience to start hosting</p>
                <Link to="/dashboard/create">
                  <Button className="bg-[#D65A31] hover:bg-[#B84A28] text-white">
                    <Plus className="w-5 h-5 mr-2" /> Create Experience
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="grid gap-6">
                {experiences.map((exp) => (
                  <div key={exp.id} className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid={`experience-${exp.id}`}>
                    <div className="flex">
                      <div className="w-48 h-36 flex-shrink-0">
                        {exp.images?.[0] ? (
                          <img src={exp.images[0]} alt={exp.title} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                            <UtensilsCrossed className="w-8 h-8 text-gray-400" />
                          </div>
                        )}
                      </div>
                      <div className="flex-1 p-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-lg">{exp.title}</h3>
                            <p className="text-gray-500 text-sm">{exp.location} · {exp.cuisine}</p>
                          </div>
                          <div className="flex flex-col gap-2 items-end">
                            {/* Admin Approval Status */}
                            <div className="flex items-center gap-2">
                              <ShieldCheck className="w-4 h-4 text-gray-400" />
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                exp.approval_status === 'approved' ? 'bg-green-100 text-green-700' :
                                exp.approval_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                exp.approval_status === 'rejected' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {exp.approval_status === 'pending' ? 'Pending Approval' :
                                 exp.approval_status === 'approved' ? 'Approved' :
                                 exp.approval_status === 'rejected' ? 'Rejected' : 'Unknown'}
                              </span>
                            </div>
                            {/* Host Bookable Toggle - only show if approved */}
                            {exp.approval_status === 'approved' && (
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Bookable:</span>
                                <Switch
                                  checked={exp.bookable !== false}
                                  onCheckedChange={() => toggleBookable(exp.id, exp.bookable !== false)}
                                  disabled={togglingBookable[exp.id]}
                                  data-testid={`bookable-toggle-${exp.id}`}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                        {exp.approval_status === 'pending' && (
                          <p className="text-yellow-600 text-xs mt-1">
                            ⏳ Your experience is awaiting admin approval before it becomes visible to guests.
                          </p>
                        )}
                        {exp.approval_status === 'rejected' && exp.admin_notes && (
                          <p className="text-red-600 text-xs mt-1">
                            Rejection reason: {exp.admin_notes}
                          </p>
                        )}
                        {exp.approval_status === 'approved' && exp.bookable === false && (
                          <p className="text-gray-500 text-xs mt-1">
                            This experience is approved but not currently accepting bookings.
                          </p>
                        )}
                        <div className="flex items-center gap-6 mt-3 text-sm text-gray-500">
                          <span className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4" /> {formatPrice(exp.price)}/person
                          </span>
                          <span className="flex items-center gap-1">
                            <Users className="w-4 h-4" /> Up to {exp.max_guests}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" /> {exp.duration}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-4 h-4" /> {exp.view_count} views
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" /> {exp.booking_count} bookings
                          </span>
                        </div>
                      </div>
                      <div className="p-4 flex items-center gap-2">
                        <Link to={`/dashboard/edit/${exp.id}`}>
                          <Button variant="outline" size="sm" data-testid={`edit-${exp.id}`}>
                            <Edit className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeleteId(exp.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-[#FDF8F4]"
                          data-testid={`delete-${exp.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <div className="space-y-6" data-testid="bookings-tab">
            {bookings.length === 0 ? (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h2 className="text-xl font-semibold mb-2">No bookings yet</h2>
                <p className="text-gray-500">When guests book your experiences, they'll appear here</p>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-4 font-medium">Guest</th>
                      <th className="text-left p-4 font-medium">Experience</th>
                      <th className="text-left p-4 font-medium">Date</th>
                      <th className="text-left p-4 font-medium">Guests</th>
                      <th className="text-left p-4 font-medium">Total</th>
                      <th className="text-left p-4 font-medium">Status</th>
                      <th className="text-left p-4 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {bookings.map((booking) => (
                      <tr key={booking.id} data-testid={`booking-${booking.id}`}>
                        <td className="p-4">
                          <p className="font-medium">{booking.guest_name}</p>
                          <p className="text-sm text-gray-500">{booking.guest_email}</p>
                        </td>
                        <td className="p-4 text-sm">{booking.experience_title}</td>
                        <td className="p-4 text-sm">{booking.date}</td>
                        <td className="p-4 text-sm">{booking.guests}</td>
                        <td className="p-4 font-medium">{formatPrice(booking.total_price)}</td>
                        <td className="p-4">
                          <span className={`px-3 py-1 rounded-full text-sm ${
                            booking.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            booking.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {booking.status}
                          </span>
                        </td>
                        <td className="p-4">
                          {booking.status === 'pending' && (
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => handleUpdateBookingStatus(booking.id, 'confirmed')}
                                data-testid={`confirm-${booking.id}`}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 hover:bg-[#FDF8F4]"
                                onClick={() => handleUpdateBookingStatus(booking.id, 'cancelled')}
                                data-testid={`cancel-${booking.id}`}
                              >
                                <XCircle className="w-4 h-4" />
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Earnings Tab */}
        {activeTab === 'earnings' && (
          <div className="space-y-6" data-testid="earnings-tab">
            {/* Earnings Summary Cards */}
            {earnings && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <DollarSign className="w-8 h-8 text-[#D65A31]" />
                  </div>
                  <p className="text-3xl font-bold" data-testid="gross-revenue">{formatPrice(earnings.total_gross)}</p>
                  <p className="text-gray-500 text-sm">Gross Revenue</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <ArrowDownToLine className="w-8 h-8 text-amber-500" />
                  </div>
                  <p className="text-3xl font-bold" data-testid="commission">{formatPrice(earnings.total_commission)}</p>
                  <p className="text-gray-500 text-sm">Commission ({Math.round(earnings.commission_rate * 100)}%)</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <Wallet className="w-8 h-8 text-green-600" />
                  </div>
                  <p className="text-3xl font-bold text-green-700" data-testid="net-earnings">{formatPrice(earnings.total_net)}</p>
                  <p className="text-gray-500 text-sm">Your Net Earnings</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-3">
                    <Clock className="w-8 h-8 text-blue-500" />
                  </div>
                  <p className="text-3xl font-bold" data-testid="pending-payout">{formatPrice(earnings.total_pending_payout)}</p>
                  <p className="text-gray-500 text-sm">Pending Payout</p>
                </div>
              </div>
            )}

            {/* Bank Details Form */}
            <div className="bg-white rounded-xl shadow-sm" data-testid="bank-details-section">
              <div className="p-6 border-b flex items-center gap-3">
                <Landmark className="w-5 h-5 text-[#D65A31]" />
                <h2 className="text-lg font-semibold">Bank Details for Payouts</h2>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-500 mb-5">Add your bank account details so we can send you your earnings. Payouts are processed regularly.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                    <input type="text" value={bankDetails.bank_name} onChange={(e) => setBankDetails(p => ({ ...p, bank_name: e.target.value }))} placeholder="e.g. Barclays, HSBC" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D65A31]" data-testid="bank-name-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name</label>
                    <input type="text" value={bankDetails.account_holder} onChange={(e) => setBankDetails(p => ({ ...p, account_holder: e.target.value }))} placeholder="Full name on account" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D65A31]" data-testid="account-holder-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                    <input type="text" value={bankDetails.account_number} onChange={(e) => setBankDetails(p => ({ ...p, account_number: e.target.value }))} placeholder="12345678" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D65A31]" data-testid="account-number-input" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Sort Code</label>
                    <input type="text" value={bankDetails.sort_code} onChange={(e) => setBankDetails(p => ({ ...p, sort_code: e.target.value }))} placeholder="12-34-56" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#D65A31]" data-testid="sort-code-input" />
                  </div>
                </div>
                <Button onClick={saveBankDetails} disabled={bankSaving} className="mt-5 bg-[#D65A31] hover:bg-[#B84A28] text-white" data-testid="save-bank-btn">
                  {bankSaving ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> Save Bank Details</>}
                </Button>
              </div>
            </div>

            {/* Earnings Breakdown Table */}
            {earnings && earnings.bookings.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="earnings-breakdown">
                <div className="p-6 border-b">
                  <h2 className="text-lg font-semibold">Earnings Breakdown</h2>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left p-4 font-medium text-sm">Experience</th>
                        <th className="text-left p-4 font-medium text-sm">Guest</th>
                        <th className="text-left p-4 font-medium text-sm">Date</th>
                        <th className="text-right p-4 font-medium text-sm">Gross</th>
                        <th className="text-right p-4 font-medium text-sm">Commission</th>
                        <th className="text-right p-4 font-medium text-sm">Your Earnings</th>
                        <th className="text-center p-4 font-medium text-sm">Payout</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {earnings.bookings.map((b) => (
                        <tr key={b.booking_id}>
                          <td className="p-4 text-sm max-w-[200px] truncate">{b.experience_title}</td>
                          <td className="p-4 text-sm">{b.guest_name}</td>
                          <td className="p-4 text-sm">{b.date}</td>
                          <td className="p-4 text-sm text-right">{formatPrice(b.gross)}</td>
                          <td className="p-4 text-sm text-right text-amber-600">-{formatPrice(b.commission)}</td>
                          <td className="p-4 text-sm text-right font-semibold text-green-700">{formatPrice(b.net)}</td>
                          <td className="p-4 text-center">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${b.payout_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                              {b.payout_status === 'paid' ? 'Paid' : 'Pending'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {earnings && earnings.bookings.length === 0 && (
              <div className="bg-white rounded-xl p-12 text-center shadow-sm">
                <Wallet className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h2 className="text-xl font-semibold mb-2">No earnings yet</h2>
                <p className="text-gray-500">When guests pay for your experiences, your earnings will appear here</p>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Experience?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete this experience and all its data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteExperience}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DashboardPage;
